import { useEffect, useRef } from 'react';
import styles from './CandleSVG.module.css';

interface CandleSVGProps {
  /** 'idle' = unlit, 'burning' = wax actively descending, 'finished' = wick exposed, flame dying. */
  state: 'idle' | 'burning' | 'finished';
  /** Duration of the burn in milliseconds. Drives the CSS transition timing. */
  burnDurationMs: number;
  /** Called when the wax-descent transition completes naturally. */
  onBurnComplete?: () => void;
  /** Click handler — only fires in 'idle' state. */
  onLight?: () => void;
}

const VIEWBOX_W = 240;
const VIEWBOX_H = 480;
const CANDLE_X = (VIEWBOX_W - 90) / 2;
const CANDLE_TOP = 110;
const CANDLE_BOTTOM = 440;
const CANDLE_HEIGHT = CANDLE_BOTTOM - CANDLE_TOP;
const WICK_HEIGHT = 18;
const DESCENT_PX = CANDLE_HEIGHT - 20;
const FLAME_Y_START = CANDLE_TOP - WICK_HEIGHT - 30;

/**
 * The Candle. SVG with a tall body, a wick, and a flame at the top.
 *
 * Burn mechanic:
 *   - A "burned overlay" rect grows from the top via scaleY (0 → 1)
 *     over `burnDurationMs`, visually shortening the candle.
 *   - The wick + flame translate downward at the same rate so the
 *     flame always rides the top of the remaining wax.
 *   - Inner flame group flickers independently via CSS keyframe.
 */
export function CandleSVG({ state, burnDurationMs, onBurnComplete, onLight }: CandleSVGProps) {
  const overlayRef = useRef<SVGRectElement>(null);

  useEffect(() => {
    const el = overlayRef.current;
    if (!el || state !== 'burning' || !onBurnComplete) return;
    function handle(ev: TransitionEvent) {
      if (ev.propertyName === 'transform') onBurnComplete?.();
    }
    el.addEventListener('transitionend', handle);
    return () => el.removeEventListener('transitionend', handle);
  }, [state, onBurnComplete]);

  const handleClick = () => {
    if (state === 'idle') onLight?.();
  };

  const inlineDuration = { transitionDuration: `${burnDurationMs}ms` };
  const isBurning = state === 'burning';
  const isFinished = state === 'finished';

  return (
    <svg
      viewBox={`0 0 ${VIEWBOX_W} ${VIEWBOX_H}`}
      className={`${styles.svg} ${state === 'idle' ? styles.clickable : ''}`}
      onClick={handleClick}
      role={state === 'idle' ? 'button' : 'img'}
      tabIndex={state === 'idle' ? 0 : -1}
      onKeyDown={(e) => {
        if (state === 'idle' && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onLight?.();
        }
      }}
      aria-label={
        state === 'idle'
          ? 'an unlit candle, ready to begin'
          : state === 'burning'
          ? 'a candle is burning'
          : 'the candle has finished'
      }
    >
      <defs>
        <radialGradient id="hnn-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#FFC97A" stopOpacity="0.55" />
          <stop offset="40%" stopColor="#FFB347" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#FFB347" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="hnn-wax" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#D9C7A4" />
          <stop offset="50%" stopColor="#F5E6D3" />
          <stop offset="100%" stopColor="#B8A77F" />
        </linearGradient>
        <radialGradient id="hnn-ambient" cx="50%" cy="55%" r="60%">
          <stop offset="0%" stopColor="#2D0E29" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#0F0A1F" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* ambient warmth behind the candle */}
      <rect
        x="0"
        y="0"
        width={VIEWBOX_W}
        height={VIEWBOX_H}
        fill="url(#hnn-ambient)"
        className={`${styles.ambient} ${state !== 'idle' ? styles.ambientLit : ''}`}
      />

      {/* candle body — full height, always */}
      <rect
        x={CANDLE_X}
        y={CANDLE_TOP}
        width="90"
        height={CANDLE_HEIGHT}
        fill="url(#hnn-wax)"
        rx="3"
      />

      {/* highlight stripe */}
      <rect
        x={CANDLE_X + 8}
        y={CANDLE_TOP + 4}
        width="6"
        height={CANDLE_HEIGHT - 8}
        fill="#FFFFFF"
        fillOpacity="0.10"
        rx="3"
      />

      {/* burned-overlay — grows from top downward to "consume" the candle */}
      <rect
        ref={overlayRef}
        x={CANDLE_X - 1}
        y={CANDLE_TOP - 2}
        width="92"
        height={DESCENT_PX}
        fill="var(--hnn-midnight)"
        className={`${styles.overlay} ${isBurning ? styles.overlayBurning : ''} ${
          isFinished ? styles.overlayFinished : ''
        }`}
        style={inlineDuration}
      />

      {/* wick + flame group — descends to track top of remaining wax */}
      <g
        className={`${styles.wickFlame} ${isBurning ? styles.wickFlameBurning : ''}`}
        style={inlineDuration}
      >
        <line
          x1={VIEWBOX_W / 2}
          y1={CANDLE_TOP - WICK_HEIGHT}
          x2={VIEWBOX_W / 2}
          y2={CANDLE_TOP + 2}
          stroke="#1a1410"
          strokeWidth="2"
          strokeLinecap="round"
        />

        {!isFinished && (
          <>
            <circle
              cx={VIEWBOX_W / 2}
              cy={FLAME_Y_START + 15}
              r="80"
              fill="url(#hnn-glow)"
              className={`${styles.glow} ${isBurning ? styles.glowLit : ''}`}
            />
            {/* Outer positioning group holds the SVG translate that
             * pins the flame to the wick. Inner group gets the CSS
             * flicker animation. Splitting them is the critical fix:
             * when the flicker keyframes set
             * `transform: translate(0, 0) scale(...)`, that CSS
             * transform was overriding the SVG transform attribute on
             * the same element, jumping the flame to viewBox (0, 0)
             * (visible as a tiny flame at the top-left of the
             * candle frame). With the outer translate on its own
             * group, the inner animation can run without losing
             * position. */}
            <g transform={`translate(${VIEWBOX_W / 2 - 12} ${FLAME_Y_START})`}>
              <g
                className={`${styles.flameFlicker} ${isBurning ? styles.flameFlickerLit : ''}`}
              >
                {/* outer warm flame */}
                <path
                  d="M12 0 C18 8 22 16 18 28 C16 34 8 34 6 28 C2 16 6 8 12 0 Z"
                  fill="#FFA53A"
                  opacity="0.85"
                />
                {/* inner bright core */}
                <path
                  d="M12 6 C15 12 17 18 14 25 C13 28 11 28 10 25 C7 18 9 12 12 6 Z"
                  fill="#FFE082"
                />
                {/* hot blue base */}
                <ellipse cx="12" cy="29" rx="4" ry="2" fill="#7FA8E6" opacity="0.7" />
              </g>
            </g>
          </>
        )}

        {isFinished && (
          <path
            d={`M${VIEWBOX_W / 2} ${CANDLE_TOP - WICK_HEIGHT}
                c -6 -14, 8 -22, 0 -38
                c -8 -16, 10 -22, 0 -38`}
            stroke="#C8B89A"
            strokeOpacity="0.4"
            strokeWidth="2"
            fill="none"
            className={styles.smoke}
          />
        )}
      </g>
    </svg>
  );
}
